/**
 * Sortierung, Filter und Formatierung für die Projekt-Listenansicht.
 *
 * Sortier- und Filterzustand lebt in der URL, damit eine gefilterte Liste
 * teilbar ist und der Zurück-Button sie wiederherstellt.
 */

import type { Lead } from '../../../types';
import { formatDateToDDMMYYYY } from '../../../utils/date';
import { leadListAmount } from './projectValue';

export type LeadSortKey =
  | 'title'
  | 'category'
  | 'contact'
  | 'address'
  | 'amount'
  | 'status'
  | 'created';

export type LeadSortDirection = 'asc' | 'desc';

export type LeadSort = { key: LeadSortKey; direction: LeadSortDirection };

export const DEFAULT_LEAD_SORT: LeadSort = { key: 'created', direction: 'desc' };

const SORT_KEYS: LeadSortKey[] = [
  'title',
  'category',
  'contact',
  'address',
  'amount',
  'status',
  'created',
];

/** Spalten, die absteigend starten — bei Datum und Betrag ist „größtes zuerst“ erwartbar. */
const DESC_FIRST_KEYS = new Set<LeadSortKey>(['created', 'amount']);

export function defaultDirectionForSortKey(key: LeadSortKey): LeadSortDirection {
  return DESC_FIRST_KEYS.has(key) ? 'desc' : 'asc';
}

/** `titel:asc` → `{ key: 'title', direction: 'asc' }`; ungültige Werte fallen auf die Standardsortierung zurück. */
export function parseLeadSort(raw: string | null | undefined): LeadSort {
  const [rawKey, rawDirection] = String(raw ?? '').split(':');
  const key = SORT_KEYS.find((k) => k === rawKey);
  if (!key) return DEFAULT_LEAD_SORT;
  const direction: LeadSortDirection = rawDirection === 'asc' ? 'asc' : 'desc';
  return { key, direction };
}

/** Null, wenn die Standardsortierung aktiv ist — dann bleibt die URL sauber. */
export function serializeLeadSort(sort: LeadSort): string | null {
  if (sort.key === DEFAULT_LEAD_SORT.key && sort.direction === DEFAULT_LEAD_SORT.direction) {
    return null;
  }
  return `${sort.key}:${sort.direction}`;
}

const collator = new Intl.Collator('de', { sensitivity: 'base', numeric: true });

const textValue = (lead: Lead, key: LeadSortKey): string => {
  switch (key) {
    case 'title':
      return lead.lead_title ?? '';
    case 'category':
      return lead.category ?? '';
    case 'contact':
      return lead.name ?? '';
    case 'address':
      return lead.address ?? '';
    case 'status':
      return lead.status ?? '';
    default:
      return '';
  }
};

/**
 * Stabil sortierte Kopie. Leere Werte landen unabhängig von der Richtung
 * am Ende, sonst füllt sich der Anfang der Liste mit „—“.
 */
export function sortLeads(leads: Lead[], sort: LeadSort): Lead[] {
  const factor = sort.direction === 'asc' ? 1 : -1;

  return leads
    .map((lead, index) => ({ lead, index }))
    .sort((a, b) => {
      let result = 0;

      if (sort.key === 'created') {
        const at = Date.parse(a.lead.created_at ?? '');
        const bt = Date.parse(b.lead.created_at ?? '');
        const aValid = Number.isFinite(at);
        const bValid = Number.isFinite(bt);
        if (!aValid || !bValid) result = aValid ? -1 : bValid ? 1 : 0;
        else result = (at - bt) * factor;
      } else if (sort.key === 'amount') {
        const av = leadListAmount(a.lead);
        const bv = leadListAmount(b.lead);
        const aEmpty = av == null || !Number.isFinite(av);
        const bEmpty = bv == null || !Number.isFinite(bv);
        if (aEmpty || bEmpty) result = aEmpty && bEmpty ? 0 : aEmpty ? 1 : -1;
        else result = (av - bv) * factor;
      } else {
        const av = textValue(a.lead, sort.key).trim();
        const bv = textValue(b.lead, sort.key).trim();
        if (!av || !bv) result = av === bv ? 0 : av ? -1 : 1;
        else result = collator.compare(av, bv) * factor;
      }

      return result !== 0 ? result : a.index - b.index;
    })
    .map((entry) => entry.lead);
}

export type LeadDateRange = 'all' | '7d' | '30d' | '90d' | '365d';

export const LEAD_DATE_RANGE_OPTIONS: Array<{ value: LeadDateRange; label: string }> = [
  { value: 'all', label: 'Gesamter Zeitraum' },
  { value: '7d', label: 'Letzte 7 Tage' },
  { value: '30d', label: 'Letzte 30 Tage' },
  { value: '90d', label: 'Letzte 90 Tage' },
  { value: '365d', label: 'Letzte 12 Monate' },
];

const DATE_RANGE_DAYS: Record<Exclude<LeadDateRange, 'all'>, number> = {
  '7d': 7,
  '30d': 30,
  '90d': 90,
  '365d': 365,
};

export function parseLeadDateRange(raw: string | null | undefined): LeadDateRange {
  const value = String(raw ?? '');
  return LEAD_DATE_RANGE_OPTIONS.some((o) => o.value === value)
    ? (value as LeadDateRange)
    : 'all';
}

export function matchesLeadDateRange(createdAt: string, range: LeadDateRange, now = Date.now()): boolean {
  if (range === 'all') return true;
  const created = Date.parse(createdAt);
  if (!Number.isFinite(created)) return false;
  return now - created <= DATE_RANGE_DAYS[range] * 24 * 60 * 60 * 1000;
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Relatives Datum für die Liste („vor 3 Tagen“); älteres bleibt als
 * absolutes Datum stehen, weil „vor 214 Tagen“ niemandem hilft.
 */
export function formatRelativeDate(iso: string, now = new Date()): string {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return '—';

  const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((startOfDay(now) - startOfDay(parsed)) / MS_PER_DAY);

  if (days < 0) return formatDateToDDMMYYYY(iso.slice(0, 10));
  if (days === 0) return 'Heute';
  if (days === 1) return 'Gestern';
  if (days < 7) return `vor ${days} Tagen`;
  if (days < 28) {
    const weeks = Math.floor(days / 7);
    return weeks === 1 ? 'vor 1 Woche' : `vor ${weeks} Wochen`;
  }
  return formatDateToDDMMYYYY(iso.slice(0, 10));
}

export function formatNetEuro(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—';
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

const CSV_COLUMNS: Array<{ header: string; value: (lead: Lead) => string }> = [
  { header: 'ID', value: (l) => l.display_id ?? '' },
  { header: 'Titel', value: (l) => l.lead_title ?? '' },
  { header: 'Kategorie', value: (l) => l.category ?? '' },
  { header: 'Kontakt', value: (l) => l.name ?? '' },
  { header: 'E-Mail', value: (l) => l.email ?? '' },
  { header: 'Baustelle', value: (l) => l.address ?? '' },
  {
    header: 'Betrag netto',
    value: (l) => {
      const amount = leadListAmount(l);
      return amount == null || !Number.isFinite(amount) ? '' : String(amount).replace('.', ',');
    },
  },
  { header: 'Status', value: (l) => l.status ?? '' },
  { header: 'Erstellt', value: (l) => formatDateToDDMMYYYY(String(l.created_at ?? '').slice(0, 10)) },
];

const escapeCsvCell = (raw: string): string => `"${raw.replace(/"/g, '""').replace(/\r?\n/g, ' ')}"`;

/** Semikolon-getrennt mit BOM — so öffnet Excel (de) die Datei ohne Import-Dialog. */
export function leadsToCsv(leads: Lead[]): string {
  const rows = [
    CSV_COLUMNS.map((c) => escapeCsvCell(c.header)).join(';'),
    ...leads.map((lead) => CSV_COLUMNS.map((c) => escapeCsvCell(c.value(lead))).join(';')),
  ];
  return `\uFEFF${rows.join('\r\n')}\r\n`;
}

export function downloadCsv(filename: string, csv: string): void {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
